// ============================================================================
// TEMPLATE REVIEW QUEUE PAGE
// Admin interface for reviewing template publishing submissions
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, XCircle, Eye, Clock, FileText, Layout, AlertCircle,
  ChevronRight, X, Layers
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  TemplateReviewQueueEntry,
  SessionTemplate,
  PhaseTemplate
} from '../types';
import {
  getPendingReviews,
  approveTemplateReview,
  rejectTemplateReview,
  getSessionTemplate,
  getPhaseTemplate
} from '../services/templateService';

export default function TemplateReviewQueue() {
  const { user } = useAuth();

  const [reviews, setReviews] = useState<TemplateReviewQueueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'ALL' | 'PHASE' | 'SESSION'>('ALL');

  const [selectedReview, setSelectedReview] = useState<TemplateReviewQueueEntry | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<SessionTemplate | PhaseTemplate | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getPendingReviews(filterType === 'ALL' ? undefined : filterType);
      setReviews(result);
    } catch (err: any) {
      console.error('Failed to load reviews:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to view the template review queue. TEMPLATE_ADMIN or SYSTEM_ADMIN role is required.');
      } else if (err.response?.status === 401) {
        setError('You are not authenticated. Please log in again.');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to load reviews');
      }
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleViewDetails = async (review: TemplateReviewQueueEntry) => {
    try {
      let template;
      if (review.templateType === 'SESSION') {
        template = await getSessionTemplate(review.templateId, review.versionNumber);
      } else {
        template = await getPhaseTemplate(review.templateId, review.versionNumber);
      }
      setSelectedReview(review);
      setSelectedTemplate(template);
      setShowDetailModal(true);
    } catch (err: any) {
      alert(`Failed to load template details: ${err.message}`);
    }
  };

  const handleApprove = async (review: TemplateReviewQueueEntry) => {
    const notes = window.prompt(`Approve "${review.templateId}"? Add review notes (optional):`);
    if (notes === null) return;
    try {
      await approveTemplateReview(review.templateType, review.reviewId, notes || undefined);
      alert('Template approved and added to organization catalog!');
      fetchReviews();
      setShowDetailModal(false);
    } catch (err: any) {
      alert(`Failed to approve: ${err.message}`);
    }
  };

  const handleReject = async (review: TemplateReviewQueueEntry) => {
    const reason = window.prompt(`Reject "${review.templateId}"? Enter rejection reason (required):`);
    if (!reason) { alert('Rejection reason is required'); return; }
    try {
      await rejectTemplateReview(review.templateType, review.reviewId, reason);
      alert('Template submission rejected');
      fetchReviews();
      setShowDetailModal(false);
    } catch (err: any) {
      alert(`Failed to reject: ${err.message}`);
    }
  };

  const stats = {
    pending: reviews.filter(r => r.status === 'PENDING').length,
    phase: reviews.filter(r => r.templateType === 'PHASE').length,
    session: reviews.filter(r => r.templateType === 'SESSION').length,
  };

  const FILTER_TABS: { key: 'ALL' | 'PHASE' | 'SESSION'; label: string; icon?: typeof FileText }[] = [
    { key: 'ALL', label: 'All' },
    { key: 'PHASE', label: 'Phase', icon: FileText },
    { key: 'SESSION', label: 'Session', icon: Layout },
  ];

  return (
    <div className="min-h-screen bg-background-light">
      <div className="w-full max-w-[1400px] mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-text-sub mb-6">
          <button onClick={() => window.history.back()} className="hover:text-primary transition-colors">Admin</button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-text-main font-medium">Template Review Queue</span>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-main mb-2">Template Review Queue</h1>
          <p className="text-text-sub">Review and approve phase and session template submissions for your organization</p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {FILTER_TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setFilterType(key)}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                filterType === key
                  ? 'bg-navy text-white shadow-sm'
                  : 'bg-white border border-slate-200 text-text-main hover:bg-slate-50'
              }`}
            >
              {Icon && <Icon className="w-4 h-4" />}
              {label}
            </button>
          ))}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-amber-50 rounded-lg">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-text-sub">Pending</span>
            </div>
            <p className="text-3xl font-bold text-text-main">{stats.pending}</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-emerald-50 rounded-lg">
                <FileText className="w-5 h-5 text-emerald-500" />
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-text-sub">Phase Templates</span>
            </div>
            <p className="text-3xl font-bold text-text-main">{stats.phase}</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Layout className="w-5 h-5 text-blue-500" />
              </div>
              <span className="text-xs font-bold uppercase tracking-wider text-text-sub">Session Templates</span>
            </div>
            <p className="text-3xl font-bold text-text-main">{stats.session}</p>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-10 h-10 border-4 border-slate-200 border-t-primary rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm text-text-sub">Loading reviews...</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-700">Error loading reviews</p>
              <p className="text-sm text-red-600 mt-1">{error}</p>
              {user && (
                <div className="mt-3 bg-red-100/50 rounded-lg p-3 text-xs text-red-600">
                  <p><strong>Current User:</strong> {user.email || user.userId}</p>
                  <p><strong>Role:</strong> {user.role || 'unknown'}</p>
                  <p className="mt-1 opacity-75">Required role: TEMPLATE_ADMIN or SYSTEM_ADMIN</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reviews Table */}
        {!loading && !error && reviews.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Template</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Type</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Submitted By</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Date</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Version</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Notes</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {reviews.map((review) => (
                  <tr key={review.reviewId} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          review.templateType === 'PHASE' ? 'bg-emerald-50' : 'bg-blue-50'
                        }`}>
                          {review.templateType === 'PHASE'
                            ? <FileText className="w-4 h-4 text-emerald-500" />
                            : <Layout className="w-4 h-4 text-blue-500" />
                          }
                        </div>
                        <p className="text-sm font-semibold text-text-main truncate max-w-[220px]">
                          {review.templateId}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                        review.templateType === 'PHASE'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {review.templateType}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-text-sub">{review.submittedBy}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-text-sub">{new Date(review.submittedAt).toLocaleDateString()}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-text-sub">v{review.versionNumber}</span>
                    </td>
                    <td className="px-6 py-5">
                      {review.submissionNotes ? (
                        <p className="text-sm text-text-sub italic truncate max-w-[180px]">"{review.submissionNotes}"</p>
                      ) : (
                        <span className="text-sm text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-6 py-5 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleViewDetails(review)}
                          className="px-3 py-2 rounded-lg bg-navy text-white text-xs font-medium hover:bg-navy-dark transition-colors flex items-center gap-1.5"
                        >
                          <Eye className="w-3.5 h-3.5" /> Review
                        </button>
                        <button
                          onClick={() => handleApprove(review)}
                          className="px-3 py-2 rounded-lg bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-600 transition-colors flex items-center gap-1.5"
                        >
                          <CheckCircle className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button
                          onClick={() => handleReject(review)}
                          className="px-3 py-2 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors flex items-center gap-1.5"
                        >
                          <XCircle className="w-3.5 h-3.5" /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && reviews.length === 0 && (
          <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-16 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-text-main mb-2">No pending reviews</h3>
            <p className="text-sm text-text-sub max-w-sm mx-auto">
              All template submissions have been reviewed. Great work!
            </p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedReview && selectedTemplate && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6"
          onClick={() => setShowDetailModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-[800px] w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  selectedReview.templateType === 'PHASE' ? 'bg-emerald-50' : 'bg-blue-50'
                }`}>
                  {selectedReview.templateType === 'PHASE'
                    ? <FileText className="w-5 h-5 text-emerald-500" />
                    : <Layout className="w-5 h-5 text-blue-500" />
                  }
                </div>
                <div>
                  <h2 className="text-lg font-bold text-text-main">
                    Review {selectedReview.templateType === 'PHASE' ? 'Phase' : 'Session'} Template
                  </h2>
                  <p className="text-sm text-text-sub">{selectedTemplate.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Template ID */}
              <div>
                <h3 className="text-sm font-semibold text-text-main mb-2">Template ID</h3>
                <p className="text-xs text-text-sub font-mono bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  {'sessionTemplateId' in selectedTemplate ? selectedTemplate.sessionTemplateId : selectedTemplate.phaseTemplateId}
                </p>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-sm font-semibold text-text-main mb-2">Description</h3>
                <p className="text-sm text-text-sub leading-relaxed">{selectedTemplate.description}</p>
              </div>

              {/* Phase-specific fields */}
              {selectedReview.templateType === 'PHASE' && 'goal' in selectedTemplate && (
                <div>
                  <h3 className="text-sm font-semibold text-text-main mb-2">Goal</h3>
                  <p className="text-sm text-text-sub leading-relaxed">{selectedTemplate.goal}</p>
                </div>
              )}

              {selectedReview.templateType === 'PHASE' && 'duration' in selectedTemplate && (
                <div>
                  <h3 className="text-sm font-semibold text-text-main mb-2">Duration</h3>
                  <p className="text-sm text-text-sub">{selectedTemplate.duration}</p>
                </div>
              )}

              {selectedReview.templateType === 'PHASE' && 'successCriteria' in selectedTemplate && selectedTemplate.successCriteria && (
                <div>
                  <h3 className="text-sm font-semibold text-text-main mb-2">Success Criteria</h3>
                  <div className="space-y-2">
                    {selectedTemplate.successCriteria.map((criterion) => (
                      <div key={criterion} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                        <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-text-main">{criterion}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Session-specific fields */}
              {selectedReview.templateType === 'SESSION' && 'phaseReferences' in selectedTemplate && (
                <div>
                  <h3 className="text-sm font-semibold text-text-main mb-2">Phases</h3>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
                    <Layers className="w-3.5 h-3.5 mr-1.5" />
                    {selectedTemplate.phaseReferences.length} phase(s) included
                  </span>
                </div>
              )}

              {/* Metadata Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Version</p>
                  <p className="text-sm font-medium text-text-main">v{selectedTemplate.versionNumber}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Scope</p>
                  <p className="text-sm font-medium text-text-main">{selectedTemplate.scope}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Status</p>
                  <p className="text-sm font-medium text-text-main">{selectedTemplate.publishingStatus}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Created</p>
                  <p className="text-sm font-medium text-text-main">{new Date(selectedTemplate.createdAt).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Submission Notes */}
              {selectedReview.submissionNotes && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-primary mb-2">Submission Notes</h3>
                  <p className="text-sm text-primary/80 italic">"{selectedReview.submissionNotes}"</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-6 border-t border-slate-200">
              <button
                onClick={() => setShowDetailModal(false)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-slate-300 text-text-main hover:bg-slate-50 font-medium transition-colors text-sm"
              >
                Close
              </button>
              <button
                onClick={() => handleApprove(selectedReview)}
                className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium transition-colors text-sm flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" /> Approve
              </button>
              <button
                onClick={() => handleReject(selectedReview)}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium transition-colors text-sm flex items-center justify-center gap-2"
              >
                <XCircle className="w-4 h-4" /> Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
